// Copyright (c) Ping. All rights reserved.

import { useCallback, useMemo, useReducer } from 'react';
import {
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
  type PollComposerAction,
  type PollComposerError,
  type PollComposerState,
  type PollComposerValidation,
  type PollDurationPreset,
  type PollKind,
} from './types';

const INITIAL_STATE: PollComposerState = {
  question: '',
  kind: 'single',
  anonymous: false,
  duration: '24h',
  options: [
    { id: 'opt-1', text: '' },
    { id: 'opt-2', text: '' },
  ],
};

let optionCounter = 2;

/** Pure reducer; exported so the tests can exercise it without mounting. */
export function pollComposerReducer(
  state: PollComposerState,
  action: PollComposerAction,
): PollComposerState {
  switch (action.type) {
    case 'setQuestion':
      return { ...state, question: action.question };
    case 'setKind':
      return { ...state, kind: action.kind };
    case 'setAnonymous':
      return { ...state, anonymous: action.anonymous };
    case 'setDuration':
      return { ...state, duration: action.duration };
    case 'addOption': {
      if (state.options.length >= POLL_MAX_OPTIONS) {
        return state;
      }
      optionCounter += 1;
      return {
        ...state,
        options: [...state.options, { id: `opt-${optionCounter}`, text: '' }],
      };
    }
    case 'removeOption': {
      const next = state.options.filter((o) => o.id !== action.id);
      if (next.length < POLL_MIN_OPTIONS) {
        return state;
      }
      return { ...state, options: next };
    }
    case 'setOptionText':
      return {
        ...state,
        options: state.options.map((o) => (o.id === action.id ? { ...o, text: action.text } : o)),
      };
    case 'reset':
      return INITIAL_STATE;
  }
}

/**
 * Pure validator — exported so tests can assert without mounting. Returns
 * the full set of errors so the host can render them in any order.
 */
export function validateComposer(state: PollComposerState): PollComposerValidation {
  const errors: PollComposerError[] = [];

  if (state.question.trim().length === 0) {
    errors.push('questionEmpty');
  }
  if (state.options.length < POLL_MIN_OPTIONS) {
    errors.push('tooFewOptions');
  }
  if (state.options.length > POLL_MAX_OPTIONS) {
    errors.push('tooManyOptions');
  }
  if (state.options.some((o) => o.text.trim().length === 0)) {
    errors.push('optionEmpty');
  }

  const seen = new Set<string>();
  let hasDuplicate = false;
  for (const o of state.options) {
    const normalised = o.text.trim().toLocaleLowerCase();
    if (normalised.length === 0) {
      continue;
    }
    if (seen.has(normalised)) {
      hasDuplicate = true;
      break;
    }
    seen.add(normalised);
  }
  if (hasDuplicate) {
    errors.push('duplicateOption');
  }

  return {
    canSubmit: errors.length === 0,
    errors,
  };
}

export interface PollComposerViewModel {
  readonly state: PollComposerState;
  readonly validation: PollComposerValidation;
  readonly setQuestion: (q: string) => void;
  readonly setKind: (k: PollKind) => void;
  readonly setAnonymous: (anonymous: boolean) => void;
  readonly setDuration: (d: PollDurationPreset) => void;
  readonly addOption: () => void;
  readonly removeOption: (id: string) => void;
  readonly setOptionText: (id: string, text: string) => void;
  readonly reset: () => void;
}

/**
 * Hook around {@link pollComposerReducer}. The composer is local-only — no
 * networking, no SDK calls — submission is handed back to the caller via the
 * validated `state`.
 */
export function usePollComposerViewModel(): PollComposerViewModel {
  const [state, dispatch] = useReducer(pollComposerReducer, INITIAL_STATE);

  const validation = useMemo(() => validateComposer(state), [state]);

  const setQuestion = useCallback(
    (question: string) => dispatch({ type: 'setQuestion', question }),
    [],
  );
  const setKind = useCallback((kind: PollKind) => dispatch({ type: 'setKind', kind }), []);
  const setAnonymous = useCallback(
    (anonymous: boolean) => dispatch({ type: 'setAnonymous', anonymous }),
    [],
  );
  const setDuration = useCallback(
    (duration: PollDurationPreset) => dispatch({ type: 'setDuration', duration }),
    [],
  );
  const addOption = useCallback(() => dispatch({ type: 'addOption' }), []);
  const removeOption = useCallback((id: string) => dispatch({ type: 'removeOption', id }), []);
  const setOptionText = useCallback(
    (id: string, text: string) => dispatch({ type: 'setOptionText', id, text }),
    [],
  );
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);

  return {
    state,
    validation,
    setQuestion,
    setKind,
    setAnonymous,
    setDuration,
    addOption,
    removeOption,
    setOptionText,
    reset,
  };
}

/**
 * Tally helper used by `PollCard` to render the bar widths. Pure and
 * exported for tests.
 */
export function pollProgressFractions(poll: {
  readonly options: ReadonlyArray<{ readonly voteCount: number }>;
  readonly totalVotes: number;
}): number[] {
  if (poll.totalVotes <= 0) {
    return poll.options.map(() => 0);
  }
  return poll.options.map((o) => Math.max(0, Math.min(1, o.voteCount / poll.totalVotes)));
}

/**
 * Pure resolver: given a `closed`-shaped poll's options, return the option
 * IDs of the winners. Used by both `PollCard` (single trophy) and
 * `PollResultsSheet` (every tied option). Useful for the client to render
 * `tied` vs `closed` consistently.
 */
export function winningOptionIds(
  options: ReadonlyArray<{ readonly id: string; readonly voteCount: number }>,
): string[] {
  if (options.length === 0) {
    return [];
  }
  let max = Number.NEGATIVE_INFINITY;
  for (const o of options) {
    if (o.voteCount > max) {
      max = o.voteCount;
    }
  }
  if (max <= 0) {
    return [];
  }
  return options.filter((o) => o.voteCount === max).map((o) => o.id);
}
